<script>
  import Avatar from "@/components/widgets/Avatar.vue";

  export default {
    components: {
      Avatar,
    },
    props: {
      users: {
        type: Array,
        default: () => [],
      },
    },
    computed: {
      sortedUsers() {
        return [...this.users].sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt));
      },
    },
    methods: {
      executorName(item) {
        const u = item?.executedBy?.userId;
        if (!u) return "-";
        return `${u.user || ""}`.trim() || u.email || "-";
      },
      formatDate(dateStr) {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleString("es-CO", { dateStyle: "long", timeStyle: "short" });
      },
      platformLabel(platform) {
        const map = { jasmin: "Jasmin", streamate: "Streamate", dual: "Ambas" };
        return map[platform] || platform || "-";
      },
      uniquePlatforms(item) {
        const platforms = [...new Set((item.users || []).map((u) => u.platform).filter(Boolean))];
        const hasDual = platforms.includes("dual");
        const resolved = hasDual ? [...new Set([...platforms.filter((p) => p !== "dual"), "jasmin", "streamate"])] : platforms;
        return resolved.map((p) => this.platformLabel(p)).join(" y ") || "-";
      },
      downloadCsv(item) {
        const statusMap = { completed: "Completado", failed: "Fallido", pending: "Pendiente", inProcess: "En proceso", running: "En proceso" };
        const headers = ["Nombre artístico", "Plataforma", "Estado", "Descripción del error", "Fecha actualización", "Ejecutado por"];
        const rows = (item.users || []).map((u) => {
          const userId = u.userId && typeof u.userId === "object" ? u.userId : {};
          return [
            userId.artisticName || userId.name || u.userId || "-",
            this.platformLabel(u.platform),
            statusMap[u.status] || u.status || "-",
            u.errorDescription || "-",
            u.updatedAt ? new Date(u.updatedAt).toLocaleString("es-CO", { dateStyle: "long", timeStyle: "short" }) : "-",
            this.executorName(item),
          ];
        });

        const csv = [headers, ...rows].map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");

        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `historial_${item._id}_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
    },
  };
</script>

<template>
  <section class="userPasswordHistories" role="list" aria-label="Historial de cambios de contraseña">
    <article
      v-for="item in sortedUsers"
      :key="item._id"
      class="userPasswordHistories__usersSubContainer"
      role="listitem"
      :aria-label="`Cambio realizado por ${executorName(item)} el ${formatDate(item.startedAt)}`"
    >
      <div class="userPasswordHistories__userInfo">
        <div class="userPasswordHistories__image">
          <Avatar :user="item.executedBy && item.executedBy.userId" aria-hidden="true" />
        </div>
        <div class="userPasswordHistories__details">
          <p class="userPasswordHistories__usersSubContainer__name">{{ executorName(item) }}</p>
          <p class="userPasswordHistories__usersSubContainer__date">{{ formatDate(item.startedAt) }}</p>
          <p class="userPasswordHistories__usersSubContainer__meta">
            {{ uniquePlatforms(item) }} &middot; <strong>{{ (item.users || []).length }}</strong> {{ (item.users || []).length === 1 ? "modelo" : "modelos" }}
            <span class="userPasswordHistories__status" :class="`userPasswordHistories__status--${item.status}`" :aria-label="`Estado: ${item.status}`">
              {{ item.status === "completed" ? "Completo" : item.status === "failed" ? "Incompleto" : "En proceso" }}
            </span>
          </p>
        </div>
      </div>

      <button class="userPasswordHistories__downloadBtn" type="button" :aria-label="`Descargar historial de ${executorName(item)}`" @click="downloadCsv(item)">
        <iconic class="userPasswordHistories__icon" name="downloadFile" />
        Descargar CSV
      </button>
    </article>
  </section>
</template>

<style lang="scss">
  .userPasswordHistories {
    gap: 12px;
    display: flex;
    flex-direction: column;
    max-height: 450px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
      height: auto;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #979797;
      border-radius: 10px;
    }

    &__icon {
      font-size: 13px;
    }

    &__usersSubContainer {
      display: flex;
      padding: 16px 14px;
      align-items: center;
      gap: 19px;
      align-self: stretch;
      width: 99%;
      justify-content: space-between;
      border-radius: 8px;
      background: #f9f9f9;
    }

    &__userInfo {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    &__details {
      display: flex;
      flex-direction: column;
    }

    &__image {
      width: 31px;
      height: 31px;
      min-width: 31px;
      flex-shrink: 0;
      border: 1.5px solid $primary;
      border-radius: 50%;
      overflow: hidden;

      .globalAvatar {
        width: 100%;
        height: 100%;
      }
    }

    &__usersSubContainer__name {
      font-family: "InterDisplay-Medium";
      font-size: 15px;
      color: #3d3d3d;
      margin: 0;
    }

    &__usersSubContainer__date {
      font-size: 12px;
      color: #3d3d3d;
      margin: 0;
    }

    &__usersSubContainer__meta {
      font-size: 12px;
      color: #595959;
      margin: 0;
    }

    &__status {
      font-size: 12px;
      padding: 1px 10px;
      border-radius: 20px;
      font-weight: 600;
      white-space: nowrap;
      margin-left: 10px;

      &--completed {
        background: #e6f4ea;
        color: #2e7d32;
      }
      &--failed {
        background: rgba(189, 9, 9, 0.08);
        color: #bd0909;
      }
      &--inProcess,
      &--running {
        background: #fff8e1;
        color: #f57f17;
      }
    }

    &__downloadBtn {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 5px 10px;
      border: 1px solid #e3e3e3;
      border-radius: 7px;
      letter-spacing: -0.6px;
      background: #fff;
      color: #3d3d3d;
      font-size: 15px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #f1f1f1;
        border-color: #d1d5db;
      }

      @media (max-width: 768px) {
        width: 100%;
        justify-content: center;
      }
    }
  }
</style>
